import { SetStateAction, useCallback, useRef, useState } from 'react';

/**
 * A React hook that provides a state value which is synced with an external prop,
 * but also allows internal updates without immediately overriding external changes.
 *
 * This hook is useful when you want to:
 *  - Use a prop as the source of truth.
 *  - Update the value internally without waiting for the parent to re-render.
 *  - Avoid extra re-renders caused by syncing external props via `useEffect`.
 *
 * @param externalValue - The external value to sync with. The hook will update
 *                        its internal state only when this value changes.
 *
 * @returns A tuple containing:
 *          1. The current state value (`T`), always reflecting the latest internal or external value.
 *          2. A stable setter function, similar to `setState` from `useState`, which accepts either:
 *             - A new value (`T`), or
 *             - An updater function `(prevValue: T) => T`.
 *
 * @example
 * const [value, setValue] = useSyncedState(props.value);
 *
 * // Updating internally
 * setValue('newValue');
 *
 * // Updating based on previous value
 * setValue(prev => prev + '!');
 *
 * // If props.value changes externally, the hook will sync automatically
 */
export const useSyncedState = <T>(externalValue: T) => {
  const [_, setInnerValue] = useState(externalValue);
  const prevExternalRef = useRef(externalValue);
  const exposedValueRef = useRef(externalValue);

  if (prevExternalRef.current !== externalValue) {
    prevExternalRef.current = externalValue;
    exposedValueRef.current = externalValue;
  }

  const setState = useCallback((updater: SetStateAction<T>) => {
    setInnerValue(() => {
      const next = typeof updater === 'function' ? (updater as (prevState: T) => T)(exposedValueRef.current) : updater;

      exposedValueRef.current = next;

      return next;
    });
  }, []);

  return [exposedValueRef.current, setState] as const;
};
